import { getDefinition } from "./catalog";
import { TERMINAL_COLORS } from "./category-style";
import { effectiveTerminals } from "./effective";
import { isJunction } from "./node-kind";
import { isAcTerminalId } from "./ac";
import { acTerminalDefaultColor, terminalDisplayPolarity } from "./terminal-display";
import type { Cable, SystemNode, Terminal } from "./types";

export const MUTED_CABLE_COLOR = "var(--color-fg-muted)";

export type CablePolarity = "positive" | "negative" | null;
type CableDisplayPolarity = CablePolarity | "ground";

function terminalAt(node: SystemNode, terminalId: string): Terminal | undefined {
  if (node.kind !== "component") return undefined;
  const def = getDefinition(node.definitionId, node.customDefinition);
  if (!def) return undefined;
  return effectiveTerminals(def, node).find((t) => t.id === terminalId);
}

function polarityFromTerminals(a?: Terminal, b?: Terminal): CableDisplayPolarity {
  const polA = a && terminalDisplayPolarity(a);
  const polB = b && terminalDisplayPolarity(b);
  if (polA === "positive" || polB === "positive") return "positive";
  if (polA === "negative" || polB === "negative") return "negative";
  if (polA === "ground" || polB === "ground") return "ground";
  return null;
}

export function resolveCablePolarity(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
): CablePolarity {
  const polarity = walk(cable, nodesById, cables, new Set());
  // Keep the existing electrical rail API separate from display colors.
  return polarity === "ground" ? "negative" : polarity;
}

export function resolveCableColor(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
): string {
  const a = nodesById[cable.fromNodeId];
  const b = nodesById[cable.toNodeId];
  const ta = a ? terminalAt(a, cable.fromTerminalId) : undefined;
  const tb = b ? terminalAt(b, cable.toTerminalId) : undefined;
  // Keep an explicit AC color separate from its electrical identity. Editing
  // a terminal's polarity is not a color control and cannot turn AC into DC.
  const acColor = walkAcColor(cable, nodesById, cables, new Set());
  if (acColor) return acColor.color;
  const pol = walk(cable, nodesById, cables, new Set());
  if (pol === "positive") return TERMINAL_COLORS.positive;
  if (pol === "negative") return TERMINAL_COLORS.negative;
  if (pol === "ground") return TERMINAL_COLORS.ground;
  const signalColor =
    (ta?.polarity === "signal" && ta.color) ||
    (tb?.polarity === "signal" && tb.color) ||
    null;
  if (signalColor) return signalColor;
  return MUTED_CABLE_COLOR;
}

export function resolveJunctionColor(
  nodeId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
): string {
  for (const c of cables) {
    if (c.fromNodeId !== nodeId && c.toNodeId !== nodeId) continue;
    const acColor = walkAcColor(c, nodesById, cables, new Set());
    if (acColor) return acColor.color;
    const pol = walk(c, nodesById, cables, new Set());
    if (pol === "positive") return TERMINAL_COLORS.positive;
    if (pol === "negative") return TERMINAL_COLORS.negative;
    if (pol === "ground") return TERMINAL_COLORS.ground;
  }
  return MUTED_CABLE_COLOR;
}

/** Recurses through junctions only — every other stud carries a catalog polarity. */
function walk(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  visited: Set<string>,
): CableDisplayPolarity {
  visited.add(cable.id);
  const a = nodesById[cable.fromNodeId];
  const b = nodesById[cable.toNodeId];
  if (!a || !b) return null;

  const direct = polarityFromTerminals(
    terminalAt(a, cable.fromTerminalId),
    terminalAt(b, cable.toTerminalId),
  );
  if (direct) return direct;

  for (const end of [a, b]) {
    if (!isJunction(end)) continue;
    for (const c of cables) {
      if (visited.has(c.id)) continue;
      if (c.fromNodeId !== end.id && c.toNodeId !== end.id) continue;
      const pol = walk(c, nodesById, cables, visited);
      if (pol) return pol;
    }
  }
  return null;
}

/** Carry conductor colour through junctions without crossing component internals.
 * Explicit user colours take precedence over defaults at either end. */
function walkAcColor(
  cable: Cable, nodes: Record<string, SystemNode>, cables: Cable[], visited: Set<string>,
): { color: string; priority: number } | undefined {
  visited.add(cable.id);
  let fallback: { color: string; priority: number } | undefined;
  for (const [id, terminalId] of [[cable.fromNodeId, cable.fromTerminalId], [cable.toNodeId, cable.toTerminalId]]) {
    const node = nodes[id];
    if (!node) continue;
    const terminal = terminalAt(node, terminalId);
    if (terminal && isAcTerminalId(terminal.id) && node.kind === "component") {
      if (terminal.color) return { color: terminal.color, priority: 3 };
      const color = acTerminalDefaultColor(terminal, getDefinition(node.definitionId, node.customDefinition));
      if (color) {
        // A named L2 branch determines the leg even when the appliance says L.
        const priority = /[-_]l$/i.test(terminal.id) ? 1 : 2;
        if (!fallback || priority > fallback.priority) fallback = { color, priority };
      }
    }
    if (!isJunction(node)) continue;
    for (const next of cables) {
      if (visited.has(next.id) || (next.fromNodeId !== id && next.toNodeId !== id)) continue;
      const found = walkAcColor(next, nodes, cables, visited);
      if (found?.priority === 3) return found;
      if (found && (!fallback || found.priority > fallback.priority)) fallback = found;
    }
  }
  return fallback;
}
